import { Component, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { redirectIfNotAdmin } from 'src/app/globals';
import { PaymentReference } from 'src/app/interfaces';
import moment from 'moment';
import { FileSaverService } from 'ngx-filesaver';
import sha1 from 'crypto-js/sha1';
import sha256 from 'crypto-js/sha256';
import { createSepaData } from 'src/app/users/dialogs/create-sepa/sepa-helpers';
import { db } from 'src/app/app.component';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-debit-orders',
  templateUrl: './debit-orders.component.html',
  styleUrls: ['./debit-orders.component.scss'],
  imports: [
    MatCard,
    MatFormField,
    MatLabel,
    MatIcon,
    MatInput,
    FormsModule,
    MatIconButton,
    MatSuffix,
    MatTooltip,
    MatButton,
    MatProgressSpinner,
  ],
})
export class DebitOrdersComponent implements OnInit {
  organisationId = localStorage.getItem('orgId');
  loadingData = signal(true);
  debitOrders = signal<PaymentReference[]>([]);
  filteredDebitOrders = signal<PaymentReference[]>([]);
  // Tracks collapsed/expanded state per debit order id ('collapsed' or 'expanded')
  isCollapsed = signal<{ [id: string]: 'collapsed' | 'expanded' }>({});
  moment = moment;
  searchQuery = signal('');
  sortOrder = signal<'asc' | 'desc'>('desc');

  constructor(
    private router: Router,
    private _FileSaverService: FileSaverService,
  ) {}

  async ngOnInit() {
    const paymentReferenceCollection = await getDocs(
      query(
        collection(db, `organisations/${this.organisationId}/paymentReferences`),
        orderBy('date', 'desc'),
      ),
    );

    const initialCollapsedState: { [id: string]: 'collapsed' | 'expanded' } = {};
    paymentReferenceCollection.forEach((doc) => {
      const paymentReference = {
        ...doc.data(),
        id: doc.id,
      } as PaymentReference;

      paymentReference.date = paymentReference.date.toDate();
      this.debitOrders.update((current) => [...current, paymentReference]);
      // Initialize collapsed state for this id if not already present
      if (!initialCollapsedState[paymentReference.id]) {
        initialCollapsedState[paymentReference.id] = 'collapsed';
      }
    });
    this.isCollapsed.set(initialCollapsedState);

    redirectIfNotAdmin(this.router);

    // Initialize filtered orders
    this.filteredDebitOrders.set([...this.debitOrders()]);
    this.applyFilters();

    this.loadingData.set(false);
  }

  applyFilters(): void {
    let filtered = [...this.debitOrders()];

    if (this.searchQuery() && this.searchQuery().trim() !== '') {
      const searchValue = this.searchQuery().trim().toLowerCase();
      filtered = filtered.filter((order) => {
        // Filter by customer name (case-insensitive search)
        const customerMatch = order.customers.some((customer) =>
          customer.name.toLowerCase().includes(searchValue),
        );

        // Filter by date - support DD-MM-YYYY format and partial matching
        let dateMatch = false;
        const orderDate = moment(order.date);
        const orderDateStr = orderDate.format('DD-MM-YYYY').toLowerCase();

        // Check if the search string matches any part of the formatted date
        dateMatch = orderDateStr.includes(searchValue);

        // Also try to parse as complete DD-MM-YYYY for exact matching
        if (!dateMatch) {
          const parsed = moment(searchValue, 'DD-MM-YYYY', true);
          if (parsed.isValid()) {
            dateMatch = orderDate.isSame(parsed, 'day');
          }
        }

        // Filter by createdBy (case-insensitive search)
        const createdByMatch =
          order.createdBy && order.createdBy.toLowerCase().includes(searchValue);

        return customerMatch || dateMatch || createdByMatch;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const dateA = moment(a.date);
      const dateB = moment(b.date);

      if (this.sortOrder() === 'asc') {
        return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
      } else {
        return dateA.isAfter(dateB) ? -1 : dateA.isBefore(dateB) ? 1 : 0;
      }
    });

    this.filteredDebitOrders.set(filtered);
  }

  toggleMenu(id: string): void {
    this.isCollapsed.update((current) => {
      const newState = { ...current };
      if (!newState[id]) {
        newState[id] = 'collapsed';
      }
      newState[id] = newState[id] === 'expanded' ? 'collapsed' : 'expanded';
      return newState;
    });
  }

  async downloadDebitOrder(index: number) {
    const debitOrderToDownload = this.filteredDebitOrders()[index];
    if (!debitOrderToDownload) {
      return;
    }

    const {
      id,
      date,
      customers,
      creditorName,
      creditorCountry,
      creditorAddress,
      creditorMunicipality,
      creditorPostal,
      creditorIban,
      creditorId,
      creditorBic,
    } = debitOrderToDownload;

    // Narrow string | undefined -> string
    if (
      !id ||
      !date ||
      !creditorName ||
      !creditorCountry ||
      !creditorAddress ||
      !creditorMunicipality ||
      !creditorPostal ||
      !creditorIban ||
      !creditorId ||
      !creditorBic
    ) {
      console.error(
        'Cannot download debit order: missing required SEPA fields',
        debitOrderToDownload,
      );
      return;
    }

    const sepaData = await createSepaData(
      id,
      date,
      customers,
      creditorName,
      creditorCountry,
      creditorAddress,
      creditorMunicipality,
      creditorPostal,
      creditorIban,
      creditorId,
      creditorBic,
    );

    console.log('sepaData', sepaData);
    this.createXmlFile(id, sepaData, `${id}.xml`);
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.applyFilters();
  }

  onFilterChange(type: string, event?: any): void {
    if (type === 'search') {
      // this.searchQuery.set(event); // Already set by ngModelChange in template
      this.applyFilters();
    }
  }

  toggleSortOrder(): void {
    this.sortOrder.update((order) => (order === 'desc' ? 'asc' : 'desc'));
    this.applyFilters();
  }

  createXmlFile(paymentReferenceId: string, data: string, fileName: string) {
    this._FileSaverService.save(new Blob([data]), fileName);
    this._FileSaverService.save(
      new Blob([`Checksum SHA-1: ${sha1(data)}\nChecksum SHA-256: ${sha256(data)}`]),
      `Checksum ${paymentReferenceId}.txt`,
    );
  }
}
